import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import {
  fetchMultisigGroupByAccount,
  fetchMultisigProposalById,
  fetchMultisigProposalVoteByVoter,
} from '@/api'
import { Breadcrumbs } from '@/common/Breadcrumbs'
import ProposalStatusBadge from '@/common/ProposalStatusBadge'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { emitter, ErrorHandler, formatAddress } from '@/helpers'
import {
  buildVoteMultisigProposalMsg,
  MultisigError,
  VoteOption,
} from '@/libs/keplr/messages'
import { queryKeys } from '@/queries/keys'
import { RoutePaths } from '@/routes'
import { UiBadge } from '@/ui/UiBadge'
import { UiButton } from '@/ui/UiButton'
import {
  UiCard,
  UiCardContent,
  UiCardDescription,
  UiCardHeader,
  UiCardTitle,
} from '@/ui/UiCard'
import UiContainer from '@/ui/UiContainer'
import { UiSkeleton } from '@/ui/UiSkeleton'

import ProposalMessageCard from './components/ProposalMessageCard'
import VotesBreakdown from './components/VotesBreakdown'
import VotingPanel from './components/VotingPanel'

export default function AdminProposal() {
  const { account, proposalId } = useParams<{
    account?: string
    proposalId: string
  }>()
  const wallet = useKeplrWallet()
  const queryClient = useQueryClient()

  const proposalQuery = useQuery({
    queryKey: queryKeys.multisig.proposalById(proposalId),
    enabled: Boolean(proposalId),
    queryFn: () => fetchMultisigProposalById(proposalId!),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const groupAccount = proposalQuery.data?.group
  const resolvedGroupAccount = account ?? groupAccount ?? null

  const groupQuery = useQuery({
    queryKey: queryKeys.multisig.groupByAccount(resolvedGroupAccount),
    enabled: Boolean(resolvedGroupAccount),
    queryFn: () => fetchMultisigGroupByAccount(resolvedGroupAccount!),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const proposal = proposalQuery.data
  const group = groupQuery.data

  const voteQuery = useQuery({
    queryKey: queryKeys.multisig.proposalVoteByVoter(
      proposalId,
      wallet.address,
    ),
    enabled: Boolean(
      proposalId && wallet.address && proposal?.status === 'SUBMITTED',
    ),
    queryFn: async () => {
      if (!proposalId || !wallet.address) return null
      try {
        return await fetchMultisigProposalVoteByVoter(
          proposalId,
          wallet.address,
        )
      } catch (error) {
        // 404 means user hasn't voted yet
        return null
      }
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const userVote = voteQuery.data
  const hasVoted = Boolean(userVote)
  const userVoteOption = userVote?.option // 'YES' or 'NO'

  const isMember = useMemo(() => {
    if (!wallet.address) return false
    if (!group?.members?.length) return false
    return group.members.includes(wallet.address)
  }, [group?.members, wallet.address])

  const voteMutation = useMutation({
    mutationFn: async (option: 0 | 1) => {
      const walletAddress = wallet.address
      if (!walletAddress) {
        throw new Error('Wallet not connected')
      }

      if (!proposalId) {
        throw new Error('Proposal ID is missing')
      }

      const parsedProposalId = BigInt(proposalId)

      const voteAny = buildVoteMultisigProposalMsg({
        creator: walletAddress,
        proposalId: parsedProposalId,
        option,
      })

      const txHash = await wallet.signAndBroadcast([voteAny])

      return { txHash }
    },
    onSuccess: async () => {
      emitter.emit('success', {
        title: 'Vote submitted',
        message: 'Your vote was successfully broadcasted',
      })

      await Promise.all([
        proposalQuery.refetch(),
        voteQuery.refetch(),
        queryClient.invalidateQueries({
          queryKey: queryKeys.multisig.proposalVotes(proposalId),
        }),
      ])
    },
    onError: error => {
      ErrorHandler.process(MultisigError.fromUnknown(error))
    },
  })

  const groupPath = useMemo(() => {
    if (!resolvedGroupAccount) return RoutePaths.Multisig
    return generatePath(RoutePaths.MultisigGroup, {
      account: resolvedGroupAccount,
    })
  }, [resolvedGroupAccount])

  const breadcrumbsItems = useMemo(() => {
    const proposalLabel = proposalId ? `Proposal #${proposalId}` : 'Proposal'

    return [
      { label: 'Home', to: RoutePaths.Root },
      { label: 'Groups', to: RoutePaths.Multisig },
      {
        label: resolvedGroupAccount
          ? formatAddress(resolvedGroupAccount)
          : 'Group',
        to: resolvedGroupAccount ? groupPath : RoutePaths.Multisig,
      },
      { label: proposalLabel },
    ]
  }, [groupPath, proposalId, resolvedGroupAccount])

  const votingEndBlock = proposal?.votingEndBlock ?? ''

  const submitApprove = () => voteMutation.mutate(VoteOption.YES)
  const submitReject = () => voteMutation.mutate(VoteOption.NO)

  return (
    <UiContainer>
      <div className='mt-26 space-y-6 px-4 md:px-0'>
        <div className='bg-background sticky top-0 z-10 flex flex-col gap-6 pt-2 pb-4'>
          <Breadcrumbs items={breadcrumbsItems} />

          <div className='flex flex-wrap items-start justify-between gap-4'>
            <div className='min-w-0 flex-1 space-y-2'>
              <h1 className='text-foreground text-xl font-semibold md:text-2xl'>
                Proposal
              </h1>
              <div className='flex flex-wrap items-center gap-2'>
                <span className='text-muted-foreground text-sm'>
                  Proposal ID:
                </span>
                <span className='text-foreground font-mono text-sm break-all'>
                  {proposalId ?? ''}
                </span>
              </div>
            </div>

            {(() => {
              if (proposalQuery.isPending) {
                return <UiSkeleton className='h-6 w-24' />
              }

              if (proposal?.status) {
                return <ProposalStatusBadge status={proposal.status} />
              }

              return (
                <UiBadge variant='outline' size='sm'>
                  Unknown
                </UiBadge>
              )
            })()}
          </div>

          <UiCard>
            <UiCardHeader className='pb-3'>
              <UiCardTitle className='text-sm'>Voting end</UiCardTitle>
              <UiCardDescription>Voting end block</UiCardDescription>
            </UiCardHeader>
            <UiCardContent>
              {(() => {
                if (proposalQuery.isPending) {
                  return <UiSkeleton className='h-4 w-40' />
                }

                return (
                  <p className='text-foreground font-mono text-sm break-all'>
                    {votingEndBlock}
                  </p>
                )
              })()}
            </UiCardContent>
          </UiCard>
        </div>

        {(() => {
          if (!proposalId) {
            return (
              <UiCard className='p-6 text-center'>
                <p className='text-foreground text-sm font-semibold'>
                  Proposal ID is missing
                </p>
              </UiCard>
            )
          }

          if (proposalQuery.isPending) {
            return (
              <UiCard className='p-6'>
                <div className='space-y-3'>
                  <UiSkeleton className='h-4 w-40' />
                  <UiSkeleton className='h-4 w-130 max-w-full' />
                  <UiSkeleton className='h-4 w-105 max-w-full' />
                </div>
              </UiCard>
            )
          }

          if (proposalQuery.isError) {
            const message =
              proposalQuery.error instanceof Error
                ? proposalQuery.error.message
                : 'Failed to load proposal'

            return (
              <UiCard className='p-6 text-center'>
                <p className='text-foreground text-sm font-semibold'>
                  {message}
                </p>
              </UiCard>
            )
          }

          if (!proposal) {
            return (
              <UiCard className='p-6 text-center'>
                <p className='text-foreground text-sm font-semibold'>
                  Proposal not found
                </p>
              </UiCard>
            )
          }

          return (
            <div className='space-y-6'>
              {(() => {
                // Show votes breakdown for all proposals (if group exists)
                if (group) {
                  return (
                    <VotesBreakdown
                      proposalId={proposalId}
                      threshold={group.threshold}
                      totalMembers={group.members?.length}
                    />
                  )
                }

                return null
              })()}

              {(() => {
                const shouldShow =
                  proposal.status === 'SUBMITTED' && wallet.isWalletConnected

                return (
                  <VotingPanel
                    isVisible={shouldShow && Boolean(group)}
                    isMember={isMember}
                    isSubmitting={voteMutation.isPending}
                    hasVoted={hasVoted}
                    userVoteOption={userVoteOption}
                    submitApprove={submitApprove}
                    submitReject={submitReject}
                  />
                )
              })()}

              {(() => {
                if (proposal.status !== 'SUBMITTED') return null
                if (!wallet.isWalletConnected) return null
                if (groupQuery.isPending) {
                  return (
                    <UiCard className='p-6'>
                      <UiSkeleton className='h-4 w-64' />
                    </UiCard>
                  )
                }

                if (groupQuery.isError) {
                  const message =
                    groupQuery.error instanceof Error
                      ? groupQuery.error.message
                      : 'Failed to load group'

                  return (
                    <UiCard className='p-6 text-center'>
                      <p className='text-foreground text-sm font-semibold'>
                        {message}
                      </p>
                    </UiCard>
                  )
                }

                if (!group) {
                  return (
                    <UiCard className='p-6 text-center'>
                      <p className='text-foreground text-sm font-semibold'>
                        Group not found
                      </p>
                    </UiCard>
                  )
                }

                return null
              })()}

              <div className='space-y-3'>
                <div className='flex items-center justify-between gap-4'>
                  <h2 className='text-foreground text-lg font-semibold'>
                    Messages
                  </h2>
                  <UiButton
                    asChild
                    type='button'
                    variant='outline2'
                    size='sm'
                    radius='full'
                  >
                    <Link
                      to={generatePath(RoutePaths.MultisigGroup, {
                        account: proposal.group,
                      })}
                    >
                      Open group
                    </Link>
                  </UiButton>
                </div>

                <div className='space-y-4'>
                  {proposal.messages.map((message, index) => (
                    <ProposalMessageCard
                      key={`${message['@type'] ?? 'msg'}:${index}`}
                      message={message}
                    />
                  ))}
                </div>
              </div>
            </div>
          )
        })()}
      </div>
      <div />
    </UiContainer>
  )
}
